import axios from "axios";
import * as Sentry from "@sentry/react";
import { isExpectedNotFound } from './expectedNotFound';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    "Content-type": "application/json",
    "Cache-Control": "no-cache, no-store",
    "Pragma": "no-cache",
  }
});

// Global error handler - will be set by ErrorContext
let globalErrorHandler = null;

/**
 * Set the global error handler (called from ErrorContext)
 */
export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
};

/**
 * Handle API error - log to Sentry and show modal
 */
const handleApiError = (error, endpoint) => {
  // Don't handle cancelled requests
  if (axios.isCancel(error)) return;
  
  const status = error.response?.status;
  const message = error.response?.data?.message || error.response?.data?.error || error.message;

  // A 404 on a link-driven lookup (a story slug, a suggestion-list token) means
  // the thing is genuinely not there — an outcome the page renders, not a fault.
  // Kept narrow on purpose: a 404 anywhere else means we called a route that
  // should exist, which is worth reporting.
  const notFoundIsAState = isExpectedNotFound(status, endpoint);

  // Log to Sentry for 4xx/5xx errors — except expected, caller-handled statuses:
  // 401 (token refresh/logout flow), 403 (RBAC), 409 (duplicate), 422 (actionable)
  const expectedStatuses = [401, 403, 409, 422];
  if (status >= 400 && !expectedStatuses.includes(status) && !notFoundIsAState) {
    Sentry.captureException(error, {
      tags: {
        endpoint: endpoint,
        statusCode: status,
      },
      extra: {
        response: error.response?.data,
        request: {
          url: error.config?.url,
          method: error.config?.method,
        }
      }
    });
  }
  
  // Show error modal for non-auth errors (auth errors trigger logout)
  // 409 = conflict/duplicate — handled by callers (e.g., bucket duplicate drop)
  // 422 = actionable error — caller knows how to handle (e.g., missing contact point)
  if (globalErrorHandler && status !== 401 && status !== 403 && status !== 409 && status !== 422 && !notFoundIsAState) {
    globalErrorHandler({
      title: status >= 500 ? 'Server Error' : 'Request Failed',
      message: message,
      statusCode: status,
      endpoint: endpoint,
    });
  }
};

// --- Frontegg token bridge ---
// http-common.js is a non-React module, so we can't use hooks directly.
// Instead, FronteggTokenSync (a React component rendered inside FronteggProvider)
// pushes the access token here whenever it changes.
let _fronteggAccessToken = null;
let _fronteggLogoutFn = null;
let _fronteggRefreshFn = null;
let _refreshInFlight = null;

/**
 * Called by FronteggTokenSync to keep the access token up-to-date.
 */
export const setFronteggAccessToken = (token) => {
  _fronteggAccessToken = token;
};

/**
 * Called by FronteggTokenSync to provide the logout function.
 */
export const setFronteggLogoutFn = (fn) => {
  _fronteggLogoutFn = fn;
};

/**
 * Called by FronteggTokenSync to provide a silent token-refresh function.
 * Used by handle401() to recover from transient 401s before forcing logout.
 */
export const setFronteggRefreshFn = (fn) => {
  _fronteggRefreshFn = fn;
};

/**
 * Trigger a silent token refresh and wait briefly for the new token
 * to be pushed back via setFronteggAccessToken.
 * Deduplicates concurrent refreshes so a burst of 401s only triggers one.
 * Returns true if the access token changed (refresh succeeded), false otherwise.
 */
const tryRefreshToken = async () => {
  if (!_fronteggRefreshFn) return false;
  if (_refreshInFlight) return _refreshInFlight;

  _refreshInFlight = (async () => {
    const prevToken = _fronteggAccessToken;
    try {
      await Promise.resolve(_fronteggRefreshFn());
    } catch (_) {
      // ignore — we'll detect failure by checking whether the token changed
    }
    // Wait up to ~2s for FronteggTokenSync to push the refreshed token.
    for (let i = 0; i < 20; i++) {
      if (_fronteggAccessToken && _fronteggAccessToken !== prevToken) return true;
      await new Promise((r) => setTimeout(r, 100));
    }
    return _fronteggAccessToken && _fronteggAccessToken !== prevToken;
  })();

  try {
    return await _refreshInFlight;
  } finally {
    _refreshInFlight = null;
  }
};

// Function to get default config with current token
const getDefaultConf = () => {
  const token = _fronteggAccessToken;
  return {
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  };
};

export const handleLogout = async () => {
  if (_fronteggLogoutFn) {
    _fronteggLogoutFn();
  } else {
    // Fallback: redirect to login
    window.location.href = '/login';
  }
};

export const menuLogout = async () => {
  if (_fronteggLogoutFn) {
    _fronteggLogoutFn();
  } else {
    window.location.href = '/login';
  }
};

/**
 * API Client Class
 * 
 * ABORT SIGNAL PATTERN:
 * - Methods that accept a 'signal' parameter can be aborted by passing an AbortController.signal
 * - Methods in the IGNORE LIST should NEVER accept or use abort signals
 * 
 * IGNORE LIST (endpoints that should never be aborted):
 * - /story-ids (getAlreadyDoneStories) - Loads independently without triggering abort logic
 * - Add any other endpoints here that should not participate in abort logic
 */
class Api {

  async _post(url, data, config = {}) {
    try {
      const response = await apiClient.post(url, data, {
        ...getDefaultConf(),
        ...config,
        signal: config.signal, // Pass the AbortSignal here
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (error.response && error.response.status === 401 && !config._retried401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          return await this._post(url, data, { ...config, _retried401: true });
        }
        if (!config.silent) {
          handleApiError(error, url);
        }
        await handleLogout();
        throw error;
      }
      if (!config.silent) {
        console.error(error);
        handleApiError(error, url);
      }
      throw error; // Rethrow to let caller handle the error as well
    }
  }

  async _put(url, data, config = {}) {
    try {
      const response = await apiClient.put(url, data, {
        ...getDefaultConf(),
        ...config,
        signal: config.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (error.response && error.response.status === 401 && !config._retried401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          return await this._put(url, data, { ...config, _retried401: true });
        }
        handleApiError(error, url);
        await handleLogout();
        throw error;
      }
      console.error(error);
      handleApiError(error, url);
      throw error;
    }
  }

  async _patch(url, data, config = {}) {
    try {
      const response = await apiClient.patch(url, data, {
        ...getDefaultConf(),
        ...config,
        signal: config.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (error.response && error.response.status === 401 && !config._retried401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          return await this._patch(url, data, { ...config, _retried401: true });
        }
        handleApiError(error, url);
        await handleLogout();
        throw error;
      }
      console.error(error);
      handleApiError(error, url);
      throw error;
    }
  }

  async _delete(url, config = {}) {
    try {
      const response = await apiClient.delete(url, {
        ...getDefaultConf(),
        ...config,
        signal: config.signal,
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (error.response && error.response.status === 401 && !config._retried401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          return await this._delete(url, { ...config, _retried401: true });
        }
        handleApiError(error, url);
        await handleLogout();
        throw error;
      }
      console.error(error);
      handleApiError(error, url);
      throw error;
    }
  }


  async _get(url, config = {}) {
    try {
      const response = await apiClient.get(url, {
        ...getDefaultConf(),
        ...config,
        signal: config.signal, // Pass the AbortSignal here
      });
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error; // Rethrow so caller knows it was cancelled
      }
      if (error.response && error.response.status === 401 && !config._retried401) {
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          return await this._get(url, { ...config, _retried401: true });
        }
        if (!config.silent) {
          console.warn('[api] request failed after 401 refresh:', url, error.response?.data);
          handleApiError(error, url);
        }
        await handleLogout();
        throw error;
      }
      // Transient gateway failures (502/503/504) usually mean the upstream
      // is mid-deploy or briefly unreachable. GETs are idempotent so retry
      // exactly once after a short delay. Skip when an AbortSignal was
      // provided so we don't keep polling after the caller cancelled.
      const status = error.response?.status;
      const isTransient = status === 502 || status === 503 || status === 504;
      if (isTransient && !config._retriedTransient && !config.signal?.aborted) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (config.signal?.aborted) throw error;
        return await this._get(url, { ...config, _retriedTransient: true });
      }
      if (!config.silent) {
        console.warn('[api] request failed:', url, error.response?.data);
        handleApiError(error, url);
      }
      throw error; // Rethrow to let caller handle the error as well
    }
  }

  async suggestConcepts(prefix) {
    return await this._post('/suggest-concepts', { prefix });
  }

  async suggestSources(prefix) {
    return await this._post('/suggest-sources', { prefix });
  }

  async searchArticles(query) {
    return await this._post('/search-articles', { query });
  }

  async searchTweets(query) {
    return await this._post('/search-tweets', { query });
  }

  async setTweetBotJob(query) {
    return await this._post('/tweet_bot', { query });
  }

  async getTwitterJobs() {
    return await this._get('/get_twitter_jobs', {});
  }

  async getTwitterAuth() {
    return await this._get('/twitter-get-request-token', {});
  }

  async getTwitterBotAccounts(query) {
    return await this._post('/get_twitter_bot_accounts', {query});
  }

  async addTwitterBotAccount(query) {
    return await this._post('/add_twitter_bot_account', {query});
  }

  // async getBriefFromArticles(query) {
  //   return await this._post('/brief-from-articles', {query});
  // }

  async mergeStories(query) {
    return await this._post('/merge-stories', {query});
  }

  async mergeStoriesStatus(jobId) {
    return await this._get(`/merge-stories/status/${jobId}`);
  }

  async getBriefFromClaims(query) {
    return await this._post('/brief-from-claims', {query});
  }

  async startSavedBriefGeneration(query) {
    return await this._post('/saved-briefs/generate', { query });
  }

  async listSavedBriefs(params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.limit) qs.append('limit', params.limit);
    if (params.offset !== undefined && params.offset !== null) qs.append('offset', params.offset);
    if (params.scope) qs.append('scope', params.scope);
    if (params.ownerId) qs.append('ownerId', params.ownerId);
    if (params.status) qs.append('status', params.status);
    if (params.language) qs.append('language', params.language);
    if (params.dateRange) qs.append('dateRange', params.dateRange);
    if (params.q !== undefined && params.q !== null) qs.append('q', params.q);
    if (params.sort) qs.append('sort', params.sort);
    if (params.contentType) qs.append('contentType', params.contentType);
    const query = qs.toString();
    return await this._get(`/saved-briefs${query ? '?' + query : ''}`, config);
  }

  async getSavedBrief(id, config = {}) {
    return await this._get(`/saved-briefs/${id}`, config);
  }

  async listSavedBriefLanguages(paramsOrConfig = {}, config = {}) {
    const hasScopeParam = Object.prototype.hasOwnProperty.call(paramsOrConfig || {}, 'scope');
    const hasOwnerIdParam = Object.prototype.hasOwnProperty.call(paramsOrConfig || {}, 'ownerId');
    const params = (hasScopeParam || hasOwnerIdParam) ? paramsOrConfig : {};
    const requestConfig = (hasScopeParam || hasOwnerIdParam) ? config : paramsOrConfig;
    const qs = new URLSearchParams();
    if (params.scope) qs.append('scope', params.scope);
    if (params.ownerId) qs.append('ownerId', params.ownerId);
    const query = qs.toString();
    return await this._get(`/saved-briefs/languages${query ? '?' + query : ''}`, requestConfig);
  }

  async listSavedBriefOwners(config = {}) {
    return await this._get('/saved-briefs/owners', config);
  }

  async updateSavedBrief(id, payload) {
    return await this._patch(`/saved-briefs/${id}`, payload || {});
  }

  async regenerateSavedBrief(id, query = {}) {
    return await this._post(`/saved-briefs/${id}/regenerate`, { query });
  }

  async retrySavedBrief(id, query = {}) {
    return await this._post(`/saved-briefs/${id}/retry`, { query });
  }

  async softDeleteSavedBrief(id) {
    return await this._post(`/saved-briefs/${id}/delete`, {});
  }

  async restoreSavedBrief(id) {
    return await this._post(`/saved-briefs/${id}/restore`, {});
  }

  async permanentlyDeleteSavedBrief(id) {
    return await this._delete(`/saved-briefs/${id}`);
  }

  async getLatestReadySavedBriefNotification(params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.since) qs.append('since', params.since);
    const query = qs.toString();
    return await this._get(`/saved-briefs/ready-latest${query ? '?' + query : ''}`, config);
  }

  async markSavedBriefNotificationClicked(id, config = {}) {
    return await this._post(`/saved-briefs/${id}/notification-clicked`, {}, config);
  }

  async markSavedBriefNotificationDismissed(id, config = {}) {
    return await this._post(`/saved-briefs/${id}/notification-dismissed`, {}, config);
  }

  /**
   * IGNORE LIST: This endpoint should never be aborted by other requests.
   * It loads independently and does not trigger abort logic for other requests.
   * Do not pass an abort signal to this method.
   */
  async getAlreadyDoneStories() {
    return await this._get('/story-ids', {});
  }

  async getPublishers() {
    return await this._get('/get_publishers', {});
  }

  async getPrs() {
    return await this._get('/get_prs', {});
  }

  async getQuery(selectedQueryId) {
    return await this._get(`/get-query/${selectedQueryId}`, {});
  }

  async getQIDs() {
    return await this._get(`/queries_ids`, {});
  }

  async queries() {
    return await this._get(`/queries`, {});
  }


  // login() removed — Frontegg handles authentication directly

  async translate(query) {
    return await this._post(`/translate`, {query});
  }

  // Fast AI-based translation (uses Gemini, avoids Google Translate rate limits)
  async translateFast(query) {
    return await this._post(`/translate-fast`, {query});
  }

  async savePrompt(query) {
    return await this._post(`/save_prompt`, {query});
  }

  async promptToAI(query) {
    return await this._post(`/prompt-to-ai`, {query});
  }

  async getArticle(query) {
    return await this._post(`/get_article`, {query});
  }

  async register(query) {
    return await this._post(`/register`, {query});
  }

  async getKeywordFromUrl(query) {
    return await this._post(`/get_keyword_from_article`, {query});
  }

  async ticker() {
    return await this._get(`/tickers`, {});
  }

  async fineTuning(query) {
    return await this._get(`/fine_tuning`, { params: query });
  }

  async updateFineTuning(query) {
    return await this._post(`/fine_tuning`, {query});
  }

  async stories(limit) {
    return await this._get(`/stories/${limit}`, {});
  }

  async claims(articleIds) {
    const params = new URLSearchParams();
    articleIds.forEach(id => params.append('article_ids[]', id));
    return await this._get(`/claims?${params.toString()}`);
  }

  async articles(articleIds) {
    const payload = { article_ids: articleIds };
    return await this._post('/articles', payload);
  }

  async articlesByStory(storyId, skip = 0, limit = 0) {
    if (!storyId || storyId === 'undefined') {
      console.error('articlesByStory called with invalid storyId:', storyId);
      return [];
    }
    const params = new URLSearchParams();
    if (skip > 0) params.append('skip', skip);
    if (limit > 0) params.append('limit', limit);
    const queryString = params.toString();
    return await this._get(`/articles/${storyId}${queryString ? '?' + queryString : ''}`, {});
  }

  /**
   * Fetch a single story by slug or ObjectId.
   * Returns { story: GroupSubject, articles: Article[] }
   */
  async storyBySlug(slugOrId, signal = null) {
    if (!slugOrId || slugOrId === 'undefined') {
      console.error('storyBySlug called with invalid slugOrId:', slugOrId);
      return { story: null, articles: [] };
    }
    return await this._get(`/story/${encodeURIComponent(slugOrId)}`, signal ? { signal } : {});
  }


  async stories_search(query, signal = null, { skip = 0, limit = 0, userInitiated = false } = {}) {
    const params = new URLSearchParams();
    params.append('query', JSON.stringify(query));
    if (skip > 0) params.append('skip', skip);
    if (limit > 0) params.append('limit', limit);
    if (userInitiated) params.append('userInitiated', '1');
    return await this._get(`/stories_search?${params.toString()}`, signal ? { signal } : {});
  }


  async setStoryCompleted(query) {
      return await this._post(`/story_completed`, {query});
  }

  /**
   * Spec §16.2 — the one-click Irrelevant action. Admin only, scoped to a
   * category. `sessionIrrelevantCount` is the browser's running count for this
   * category-search session; the server decides from it whether to return the
   * §16.4 Optimize-category trigger, so the rule lives in one place.
   */
  async markStoryIrrelevantForCategory(categoryName, storyId, payload = {}) {
    return await this._post(
      `/categories/${encodeURIComponent(categoryName)}/stories/${storyId}/irrelevant`,
      payload,
    );
  }

  async latest_story() {
    return await this._get(`/latest_story`);
  }

  // confirmation() removed — Frontegg handles email verification

  /**
   * Marks an article as hidden.
   * @param {String} articleId - The ID of the article to hide.
   * @returns {Promise<Object>} - Response from the server.
   */
  async hideArticle(articleId) {
    if (!articleId) {
      throw new Error('Article ID is required to hide an article.');
    }
    return await this._post('/article-hide', { articleId });
  }

  /**
   * Fetches all categories for dropdown.
   * @returns {Promise<Array>} - Array of { name, slug } objects
   */
  async getCategories() {
    return await this._get('/categories', {});
  }

  // ==================== CMS Publishing ====================

  /**
   * Get enabled publish clients for the publish dropdown.
   * @param {object} [config] - Optional config with AbortController signal
   * @returns {Promise<Array>}
   */
  async getPublishClients(config = {}) {
    return await this._get('/publish-clients', config);
  }

  /**
   * Publish a brief to an external CMS.
   * @param {object} data - { clientId, title, subheader, body, language, category, storyId, storyTitle, status }
   * @param {object} [config] - Optional config with AbortController signal
   * @returns {Promise<object>} - PublishedItem
   */
  async publishBrief(data, config = {}) {
    return await this._post('/publish-brief', data, config);
  }

  /**
   * Get published items history.
   * @param {object} params - { clientId, storyId, status, page, limit }
   * @param {object} [config] - Optional config with AbortController signal
   * @returns {Promise<object>} - { items, total, page, pages }
   */
  async getPublishedItems(params = {}, config = {}) {
    return await this._get('/published-items', { ...params, ...config });
  }

  // ==================== Social Posts ====================

  async generateSocialPost(data, config = {}) {
    return await this._post('/social-posts/generate', data, config);
  }

  async getSocialPosts(params = {}, config = {}) {
    const query = new URLSearchParams(params).toString();
    return await this._get(`/social-posts?${query}`, config);
  }

  async getSocialPost(id, config = {}) {
    return await this._get(`/social-posts/${id}`, config);
  }

  async updateSocialPost(id, data, config = {}) {
    return await this._put(`/social-posts/${id}`, data, config);
  }

  async updateSocialSlide(postId, slideId, data, config = {}) {
    return await this._put(`/social-posts/${postId}/slides/${slideId}`, data, config);
  }

  async reorderSocialSlides(postId, slideIds, config = {}) {
    return await this._put(`/social-posts/${postId}/slides/reorder`, { slideIds }, config);
  }

  async regenerateSocialCaption(postId, config = {}) {
    return await this._post(`/social-posts/${postId}/regenerate-caption`, {}, config);
  }

  async updateSocialPostStatus(postId, status, config = {}) {
    return await this._put(`/social-posts/${postId}/status`, { status }, config);
  }

  async renderSocialSlides(postId, { mode = 'images', gradientColors, ...config } = {}) {
    const body = { mode };
    if (gradientColors) body.gradientColors = gradientColors;
    return await this._post(`/social-posts/${postId}/render`, body, config);
  }

  async exportSocialPost(postId, config = {}) {
    return await this._post(`/social-posts/${postId}/export`, {}, config);
  }

  async getSocialPostDownload(postId, config = {}) {
    return await this._get(`/social-posts/${postId}/download`, config);
  }

  async uploadSocialAsset(postId, file, slideId = null, config = {}) {
    const formData = new FormData();
    formData.append('image', file);
    if (slideId) formData.append('slideId', slideId);
    return await this._post(`/social-posts/${postId}/assets`, formData, config);
  }

  async updateSocialAsset(postId, assetId, data, config = {}) {
    return await this._put(`/social-posts/${postId}/assets/${assetId}`, data, config);
  }

  async deleteSocialPost(postId, config = {}) {
    return await this._delete(`/social-posts/${postId}`, config);
  }

  async getSocialThemes(config = {}) {
    return await this._get('/social-themes', config);
  }

  async getSocialChannels(config = {}) {
    return await this._get('/social-channels', config);
  }

  async unscheduleSocialPost(postId, config = {}) {
    return await this._post(`/social-posts/${postId}/unschedule`, {}, config);
  }

  async getSocialPostVersion(postId, config = {}) {
    return await this._get(`/social-posts/${postId}/version`, config);
  }

  // ── Meta Integration ──

  async getMetaAuthUrl(channelId, config = {}) {
    return await this._get(`/social-channels/${channelId}/meta/auth`, config);
  }

  async getMetaStatus(channelId, config = {}) {
    return await this._get(`/social-channels/${channelId}/meta/status`, config);
  }

  async publishSocialPost(postId, config = {}) {
    return await this._post(`/social-posts/${postId}/publish`, {}, config);
  }

  async disconnectMeta(channelId, config = {}) {
    return await this._post(`/admin/social-channels/${channelId}/meta/disconnect`, {}, config);
  }

  // ── Publisher Buckets ──

  async getPublisherBuckets(config = {}) {
    return await this._get('/publisher-buckets', config);
  }

  // The caller's own buckets. Unlike getPublisherBuckets (editor-gated), this
  // is available to any authenticated user — a regular editor needs it to see
  // which bucket's reformatting applies to their drafts.
  // `reformattingOnly` narrows the result to the buckets whose reformatting the
  // caller may actually apply to their own draft — the same set the backend
  // validates a submitted targetBucketId against.
  async getMyPublisherBuckets({ reformattingOnly = false, ...config } = {}) {
    const suffix = reformattingOnly ? '?reformattingOnly=true' : '';
    return await this._get(`/publisher-buckets/mine${suffix}`, config);
  }

  async updatePublisherBucket(bucketId, data, config = {}) {
    return await this._patch(`/publisher-buckets/${bucketId}`, data, config);
  }

  /**
   * @param {object} [collectionScope] the country/language filter active when
   *   the user added the story. Recorded on the suggestion so a restricted draft
   *   can later be written from exactly those sources. Omitted when no filter
   *   was active — the server treats absent and empty the same way, as "no
   *   scope", which is NOT the same as an empty scope.
   */
  async addStoryToBucket(bucketId, storyId, collectionScope = null, config = {}, options = {}) {
    const body = { storyId };
    if (collectionScope) body.collectionScope = collectionScope;
    // The exclusion gate's override. A BOOLEAN only: the server resolves which
    // languages it is overriding and records that itself, so a client cannot
    // claim an administrator approved something they were never shown.
    if (options.overrideExclusion === true) body.overrideExclusion = true;
    if (options.screen) body.screen = options.screen;
    return await this._post(`/publisher-buckets/${bucketId}/stories`, body, config);
  }

  /**
   * Would this bucket refuse this story, and why?
   *
   * Asked BEFORE the first confirmation so the dialog can state the real
   * reason. The UI never decides this for itself: the rule lives on the server
   * and a second copy of it in the frontend would be the copy an administrator
   * actually reads.
   *
   * Returns { allowed, enforcedOnAllPaths, violations, message, bucketName }.
   * `allowed` true with a non-empty `violations` is a real state: the bucket
   * has an exclusion but has not opted in to across-the-board enforcement, so
   * the add is permitted after two warnings.
   */
  async checkBucketExclusion(bucketId, storyId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/exclusion-check/${storyId}`, config);
  }

  async getBucketStories(bucketId, params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.append('status', params.status);
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.sort) qs.append('sort', params.sort);
    if (params.query) qs.append('query', params.query);
    if (params.include_archived) qs.append('include_archived', params.include_archived);
    const q = qs.toString();
    return await this._get(`/publisher-buckets/${bucketId}/stories${q ? '?' + q : ''}`, config);
  }

  // Publisher Bucket Intelligence — proactive competitor status lookup for the
  // Create Brief flow so the UI can hide competitor articles and lock the
  // generation-method dropdown to Claim-Free before the user hits Generate.
  async getBucketStoryIntelligence(bucketId, storyId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/story-intelligence/${storyId}`, config);
  }

  async changeBucketStoryStatus(bucketStoryId, status, config = {}) {
    return await this._patch(`/publisher-bucket-stories/${bucketStoryId}/status`, { status }, config);
  }

  async assignBucketStoryOwner(bucketStoryId, ownerUserId, config = {}) {
    return await this._patch(`/publisher-bucket-stories/${bucketStoryId}/owner`, { ownerUserId }, config);
  }

  async moveBucketStory(bucketStoryId, destinationBucketId, sourceMarkStatus, config = {}, options = {}) {
    // options.removeSource implements Corrections spec 6.8 (a Move removes the
    // source Suggestion). Omitted by the legacy screen, which keeps mark-source.
    const body = {
      destinationBucketId, sourceMarkStatus, removeSource: options.removeSource === true,
    };
    if (options.overrideExclusion === true) body.overrideExclusion = true;
    return await this._post(`/publisher-bucket-stories/${bucketStoryId}/move`, body, config);
  }

  async copyBucketStory(bucketStoryId, destinationBucketId, config = {}, options = {}) {
    const body = { destinationBucketId };
    if (options.overrideExclusion === true) body.overrideExclusion = true;
    return await this._post(`/publisher-bucket-stories/${bucketStoryId}/copy`, body, config);
  }

  /**
   * PRD item 5 — suggestions this bucket removed because a new article
   * appeared in a language its collection prompts exclude.
   */
  async getBucketLanguageRemovals(bucketId, params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.since) qs.append('since', params.since);
    const q = qs.toString();
    return await this._get(`/publisher-buckets/${bucketId}/language-removals${q ? '?' + q : ''}`, config);
  }

  // `orderZone` is the zone the whole batch should end up in. Sent so a drag
  // that crosses a zone boundary re-zones the row instead of being refused;
  // omit it and the server keeps the same-zone-only rule.
  async reorderBucketStories(bucketId, orderedIds, { orderZone, ...config } = {}) {
    const body = { orderedIds };
    if (Number.isFinite(orderZone)) body.orderZone = orderZone;
    return await this._patch(`/publisher-buckets/${bucketId}/reorder`, body, config);
  }

  // §C3 — freeze a capture of this bucket's suggestions and start a media
  // analysis. 409 + MEDIA_ANALYSIS_OVER_CAP means the caller must acknowledge
  // the story cap before it will proceed.
  async createMediaAnalysis(bucketId, confirmedOverflow = false, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/media-analysis`, { confirmedOverflow }, config);
  }

  async listMediaAnalyses(bucketId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/media-analysis`, config);
  }

  // §C3 — send a draft to the bucket's resolved contacts as an immutable
  // client-facing artifact. No recipient argument: the bucket contact model is
  // the source of truth, deliberately (§7 rules out a competing contacts system).
  async sendDraftToClient(savedBriefId, config = {}) {
    return await this._post(`/saved-briefs/${savedBriefId}/send-to-client`, {}, config);
  }

  // §A2 — ids in DISPLAYED order; the server preserves their relative order.
  async moveBucketStoriesToTop(bucketId, bucketStoryIds, config = {}) {
    return await this._patch(`/publisher-buckets/${bucketId}/stories/move-to-top`, { bucketStoryIds }, config);
  }

  async exportBucketSuggestions(bucketId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/export-suggestions`, {}, config);
  }

  // ── Newsletters (admin only) ──
  // A 404 here is the signal to open first-use setup, NOT a failure, so it must
  // not raise the global error modal. This frontend's flag is `silent`;
  // `_suppressGlobalError` is the admin panel's convention and is ignored here.
  async getBucketNewsletterConfig(bucketId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/newsletter-config`, {
      silent: true,
      ...config,
    });
  }

  async generateBucketNewsletterSetup(bucketId, description, config = {}) {
    return await this._post(
      `/publisher-buckets/${bucketId}/newsletter-config/generate`,
      { description },
      config,
    );
  }

  async saveBucketNewsletterConfig(bucketId, payload, config = {}) {
    return await this._put(`/publisher-buckets/${bucketId}/newsletter-config`, payload, config);
  }

  async createBucketNewsletter(bucketId, bucketStoryIds, manualOrderLocked, config = {}) {
    return await this._post(
      `/publisher-buckets/${bucketId}/newsletters`,
      { bucketStoryIds, manualOrderLocked },
      config,
    );
  }

  async getNewsletterJob(jobId, config = {}) {
    return await this._get(`/newsletter-jobs/${jobId}`, config);
  }

  // ── In Production (Corrections spec 8) ──
  // Distinct from getInProductionDashboard, which backs the legacy screen.
  // `filters` is echoed to the bulk endpoint so "select all matching" resolves
  // server-side against exactly what the table is showing.

  async getInProduction(filters = {}, config = {}) {
    const qs = new URLSearchParams();
    if (filters.status) qs.append('status', filters.status);
    if (filters.query) qs.append('query', filters.query);
    if (filters.assigneeIds?.length) qs.append('assignee_ids', filters.assigneeIds.join(','));
    if (filters.clientId) qs.append('client_id', filters.clientId);
    if (filters.bucketId) qs.append('bucket_id', filters.bucketId);
    if (filters.writtenBy) qs.append('written_by', filters.writtenBy);
    if (filters.sort) qs.append('sort', filters.sort);
    if (filters.page) qs.append('page', filters.page);
    if (filters.limit) qs.append('limit', filters.limit);
    if (filters.includeArchived) qs.append('include_archived', 'true');
    const q = qs.toString();
    return await this._get(`/publisher-bucket-stories/in-production${q ? '?' + q : ''}`, config);
  }

  async inProductionBulk({ action, ids, scope, filters = {}, assignedAdminUserId }, config = {}) {
    return await this._post('/publisher-bucket-stories/in-production/bulk', {
      action,
      ids,
      scope,
      assignedAdminUserId,
      status: filters.status,
      query: filters.query,
      assignee_ids: filters.assigneeIds?.join(','),
      // The client/bucket strips narrow the table, so "select all matching"
      // must carry them too or a bulk action would reach rows the operator
      // cannot see.
      client_id: filters.clientId,
      bucket_id: filters.bucketId,
      written_by: filters.writtenBy,
      include_archived: filters.includeArchived ? 'true' : undefined,
    }, config);
  }

  // ── Stale-suggestion archive controls (Corrections spec 4.3) ──
  // Preview first (never mutates), then apply, with ids returned for Undo.

  async previewStaleArchive({ rule, bucketId, clientId }, config = {}) {
    const qs = new URLSearchParams({ rule });
    if (bucketId) qs.append('bucketId', bucketId);
    if (clientId) qs.append('clientId', clientId);
    return await this._get(`/publisher-buckets/stale-archive/preview?${qs.toString()}`, config);
  }

  async applyStaleArchive({ rule, bucketId, clientId }, config = {}) {
    return await this._post('/publisher-buckets/stale-archive/apply', { rule, bucketId, clientId }, config);
  }

  async undoStaleArchive(ids, config = {}) {
    return await this._post('/publisher-buckets/stale-archive/undo', { ids }, config);
  }

  // Resolved contact point for a bucket — drives the Send link enable state.
  async getBucketContactPoint(bucketId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/contact-point`, config);
  }

  // Follow-up §3.4 — the bucket's own email recipients (no client/publisher
  // fallback). Drives the Send link gate and its confirmation list.
  async getBucketRecipients(bucketId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/recipients`, config);
  }

  // Same-client duplicate pre-check (Corrections spec 5.2).
  async checkBucketDuplicate(bucketId, storyId, config = {}) {
    return await this._get(
      `/publisher-buckets/${bucketId}/duplicate-check?storyId=${encodeURIComponent(storyId)}`,
      config,
    );
  }

  // ── Scope-aware bulk actions (Corrections spec 6.7) ──
  // "Select all" must act on every record matching the active primary filter,
  // search and archive filter — not just the rendered page. The server resolves
  // the scope, so these pass the filter set instead of an id list.
  // `selection` is either { ids: [...] } or { scope: 'bucket', status, query, includeArchived }.

  _bulkBody(selection) {
    if (selection?.scope === 'bucket') {
      return {
        scope: 'bucket',
        status: selection.status || undefined,
        query: selection.query || undefined,
        include_archived: selection.includeArchived || undefined,
      };
    }
    return { publisher_bucket_story_ids: selection?.ids || [] };
  }

  async bulkStatusScoped(bucketId, selection, status, config = {}) {
    return await this._post(
      `/publisher-buckets/${bucketId}/stories/bulk-status`,
      { ...this._bulkBody(selection), status },
      config,
    );
  }

  async bulkArchiveScoped(bucketId, selection, isArchived = true, config = {}) {
    return await this._post(
      `/publisher-buckets/${bucketId}/stories/bulk-archive`,
      { ...this._bulkBody(selection), is_archived: isArchived },
      config,
    );
  }

  async bulkDeleteScoped(bucketId, selection, config = {}) {
    return await this._post(
      `/publisher-buckets/${bucketId}/stories/bulk-delete`,
      this._bulkBody(selection),
      config,
    );
  }

  // ── Publisher-wide bucket links (Corrections spec 3.1-3.5) ──
  // Used only by /publisher-buckets-new; the legacy screen does not call these.

  async getPublisherBucketLinksPreview(clientId, config = {}) {
    return await this._get(`/clients/${clientId}/bucket-links/preview`, config);
  }

  async createPublisherBucketLinks(clientId, config = {}) {
    return await this._post(`/clients/${clientId}/bucket-links`, {}, config);
  }

  async emailPublisherBucketLinks(clientId, config = {}) {
    return await this._post(`/clients/${clientId}/bucket-links/email`, {}, config);
  }

  async removeBucketStory(bucketStoryId, config = {}) {
    return await this._delete(`/publisher-bucket-stories/${bucketStoryId}`, config);
  }

  // ── Publisher Buckets — Phase 1 Addendum ──

  async bulkChangeBucketStoryStatus(bucketId, publisherBucketStoryIds, status, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/stories/bulk-status`, { publisher_bucket_story_ids: publisherBucketStoryIds, status }, config);
  }

  async advanceBucketStoryStatus(bucketStoryId, config = {}, options = {}) {
    // options.respectWritingResponsibility implements Corrections spec 6.4.
    // Omitted by the legacy screen, which keeps the single production cycle.
    return await this._post(
      `/publisher-bucket-stories/${bucketStoryId}/advance-status`,
      { respectWritingResponsibility: options.respectWritingResponsibility === true },
      config,
    );
  }

  async archiveBucketStory(bucketStoryId, config = {}) {
    return await this._post(`/publisher-bucket-stories/${bucketStoryId}/archive`, {}, config);
  }

  async unarchiveBucketStory(bucketStoryId, config = {}) {
    return await this._post(`/publisher-bucket-stories/${bucketStoryId}/unarchive`, {}, config);
  }

  async bulkArchiveBucketStories(bucketId, publisherBucketStoryIds, isArchived = true, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/stories/bulk-archive`, {
      publisher_bucket_story_ids: publisherBucketStoryIds,
      is_archived: isArchived,
    }, config);
  }

  async assignBucketStoryAdmin(bucketStoryId, assignedAdminUserId, config = {}) {
    return await this._patch(`/publisher-bucket-stories/${bucketStoryId}/assignment`, { assigned_admin_user_id: assignedAdminUserId }, config);
  }

  async changeBucketStoryProductionType(bucketStoryId, productionType, config = {}) {
    return await this._patch(`/publisher-bucket-stories/${bucketStoryId}/production-type`, { production_type: productionType }, config);
  }

  async getInProductionDashboard(params = {}, config = {}) {
    const qs = new URLSearchParams();
    qs.append('include_counts', 'true');
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.status) qs.append('status', params.status);
    const q = qs.toString();
    return await this._get(`/publisher-bucket-stories/pipeline${q ? '?' + q : ''}`, config);
  }

  async getAssignableUsers(config = {}) {
    // §3.1 — Editor-accessible list of admin users. Superseded the old
    // /admin/users?limit=500 call, which was Admin-only (403 for Editors, so
    // every assigned row rendered a literal "Assigned" fallback) and capped at
    // 200 server-side anyway. This endpoint pre-filters to admins and returns
    // { users: [{ _id, name, email }] }.
    return await this._get('/publisher-buckets/assignable-admins', config);
  }

  async getMyAssignments(params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    const q = qs.toString();
    return await this._get(`/assignments/my${q ? '?' + q : ''}`, config);
  }

  async getAllAssignments(params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    const q = qs.toString();
    return await this._get(`/assignments/all${q ? '?' + q : ''}`, config);
  }

  async getUnassignedAssignments(params = {}, config = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    const q = qs.toString();
    return await this._get(`/assignments/unassigned${q ? '?' + q : ''}`, config);
  }

  // Phase 2 — Public suggestions + Client feedback
  async getPublicSuggestions(bucketId, config = {}) {
    return await this._get(`/public/buckets/${bucketId}/suggestions`, config);
  }

  async submitClientFeedback(bucketStoryId, feedbackStatus, comment, config = {}) {
    return await this._post(`/publisher-bucket-stories/${bucketStoryId}/feedback`, {
      feedback_status: feedbackStatus,
      comment,
    }, config);
  }

  // Suggestion list snapshots
  async createSuggestionList(bucketId, autoShare = false, originAction = null, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/suggestion-lists`, {
      auto_share: autoShare,
      origin_action: originAction,
    }, config);
  }

  async getSuggestionLists(bucketId, config = {}) {
    return await this._get(`/publisher-buckets/${bucketId}/suggestion-lists`, config);
  }

  async shareSuggestionList(listId, config = {}) {
    return await this._post(`/publisher-suggestion-lists/${listId}/share`, {}, config);
  }

  async getPublicSuggestionList(publicToken, config = {}) {
    return await this._get(`/public/suggestion-lists/${publicToken}`, config);
  }

  async getPublicSharedArtifact(publicToken, config = {}) {
    return await this._get(`/public/shared-artifacts/${publicToken}`, config);
  }

  async getPublicSignal(publicToken, config = {}) {
    return await this._get(`/public/signals/${publicToken}`, config);
  }

  async getPublicSignalStory(token, config = {}) {
    return await this._get(`/public/signal-stories/${token}`, config);
  }

  async getSignalTimeline(config = {}) {
    return await this._get('/signals/timeline', config);
  }

  async submitSnapshotResponse(publicToken, itemId, responseStatus, comment, productionType, config = {}) {
    return await this._post(`/public/suggestion-lists/${publicToken}/responses`, {
      item_id: itemId,
      response_status: responseStatus,
      comment,
      production_type: productionType || undefined,
    }, config);
  }

  async sendBucketSuggestions(bucketId, listId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/send-suggestions`, { list_id: listId }, config);
  }

  // ── Self-Service Feed Builder (public, no auth — NAT-02) ──────────────────
  async getFeedBuilderOptions(config = {}) {
    return await this._get('/public/feed-builder/options', config);
  }

  // `draftToken` is authoritative; `submissionId` is still sent so a client
  // deployed before tokens existed keeps resolving to the same draft.
  async saveFeedBuilderSubmission(submissionId, answers, config = {}, draftToken = null) {
    return await this._post(
      '/public/feed-builder/submissions',
      { submissionId, draftToken, answers },
      config,
    );
  }

  // spec v8 #54 — reopen an unfinished draft. Pass `silent` so a missing or aged
  // token cannot pop the app-wide error modal over a public page.
  async getFeedBuilderDraft(draftToken, config = {}) {
    return await this._get(`/public/feed-builder/drafts/${draftToken}`, { silent: true, ...config });
  }

  async closeFeedBuilderDraft(draftToken, config = {}) {
    return await this._post(
      `/public/feed-builder/drafts/${draftToken}/close`, {}, { silent: true, ...config },
    );
  }

  async getFeedBuilderInterpretation(submissionId, config = {}) {
    return await this._get(`/public/feed-builder/submissions/${submissionId}/interpretation`, config);
  }

  async activateFeedBuilderSubmission(submissionId, config = {}) {
    return await this._post(`/public/feed-builder/submissions/${submissionId}/activate`, {}, config);
  }

  // §5.1 — the optional background PDF. multipart, so it bypasses the JSON
  // helpers and posts the FormData directly.
  async uploadFeedBuilderFile(submissionId, file, config = {}) {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post(
      `/public/feed-builder/submissions/${submissionId}/file`,
      form,
      { ...config, headers: { ...(config.headers || {}), 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  async getFeedBuilderPreview(submissionId, config = {}) {
    return await this._get(`/public/feed-builder/submissions/${submissionId}/preview`, config);
  }

  // Unsubscribe. The GET only READS — following the link must never opt anyone
  // out, because mail scanners fetch every URL in an inbound message.
  async getUnsubscribeTarget(token, config = {}) {
    return await this._get(`/public/feed-builder/unsubscribe/${token}`, config);
  }

  async confirmUnsubscribe(token, config = {}) {
    return await this._post(`/public/feed-builder/unsubscribe/${token}`, {}, config);
  }

  // ── Campaign experience (public, no auth — PRD §1–§3) ────────────────────
  // Every call passes `silent` by default: these run on a public page with no
  // authenticated shell, and the app-wide error modal reads as a crash there.
  // The campaign screens render their own failure copy instead.
  async startCampaignVisit({ audience, brief, utm, landingVariant }, config = {}) {
    return await this._post(
      '/public/campaign/visits',
      { audience, brief, utm, landingVariant },
      { silent: true, ...config },
    );
  }

  async getCampaignVisit(sessionToken, config = {}) {
    return await this._get(`/public/campaign/visits/${sessionToken}`, { silent: true, ...config });
  }

  async saveCampaignAnswer(sessionToken, answer, config = {}) {
    return await this._post(
      `/public/campaign/visits/${sessionToken}/answers`,
      answer,
      { silent: true, ...config },
    );
  }

  async retryCampaignFirstList(sessionToken, config = {}) {
    return await this._post(
      `/public/campaign/visits/${sessionToken}/retry`, {}, { silent: true, ...config },
    );
  }

  async startCampaignReport(sessionToken, storyId, config = {}) {
    return await this._post(
      `/public/campaign/visits/${sessionToken}/reports`,
      { storyId },
      { silent: true, ...config },
    );
  }

  async getCampaignReport(sessionToken, reportId, config = {}) {
    return await this._get(
      `/public/campaign/visits/${sessionToken}/reports/${reportId}`,
      { silent: true, ...config },
    );
  }

  async saveCampaignReport(sessionToken, reportId, payload, config = {}) {
    return await this._patch(
      `/public/campaign/visits/${sessionToken}/reports/${reportId}`,
      payload,
      { silent: true, ...config },
    );
  }

  async retryCampaignReport(sessionToken, reportId, config = {}) {
    return await this._post(
      `/public/campaign/visits/${sessionToken}/reports/${reportId}/retry`,
      {},
      { silent: true, ...config },
    );
  }

  async submitCampaignDemoRequest(payload, config = {}) {
    return await this._post('/public/campaign/demo-requests', payload, { silent: true, ...config });
  }

  async enrollCampaignVisit(sessionToken, payload, config = {}) {
    return await this._post(
      `/public/campaign/visits/${sessionToken}/enroll`, payload, { silent: true, ...config },
    );
  }

  async getCampaignWorkspace(sessionToken, config = {}) {
    return await this._get(
      `/public/campaign/visits/${sessionToken}/workspace`, { silent: true, ...config },
    );
  }

  async getCampaignList(sessionToken, listId, config = {}) {
    return await this._get(
      `/public/campaign/visits/${sessionToken}/lists/${listId}`, { silent: true, ...config },
    );
  }

  // ── Admin oversight of self-service buckets (ADM-03/04/05) ────────────────
  async getSelfServiceReviewQueue(config = {}) {
    return await this._get('/publisher-buckets/self-service/review-queue', config);
  }

  async markSelfServiceBucketReviewed(bucketId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/self-service-reviewed`, {}, config);
  }

  async getBucketRecommendations(bucketId, params = {}, config = {}) {
    const query = new URLSearchParams();
    if (params.includeDismissed) query.set('includeDismissed', 'true');
    if (params.generationKey) query.set('generationKey', params.generationKey);
    const qs = query.toString();
    return await this._get(`/publisher-buckets/${bucketId}/recommendations${qs ? `?${qs}` : ''}`, config);
  }

  async generateBucketRecommendations(bucketId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/recommendations/generate`, {}, config);
  }

  async refreshBucketRecommendations(bucketId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/recommendations/refresh`, {}, config);
  }

  async dismissBucketRecommendation(bucketId, recommendationId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/recommendations/${recommendationId}/dismiss`, {}, config);
  }

  async addRecommendationToBucket(bucketId, recommendationId, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/recommendations/${recommendationId}/add-to-bucket`, {}, config);
  }

  async addSelectedRecommendationsToBucket(bucketId, recommendationIds, config = {}) {
    return await this._post(`/publisher-buckets/${bucketId}/recommendations/add-selected-to-bucket`, { recommendationIds }, config);
  }

  async getClients(config = {}) {
    return await this._get('/clients', config);
  }

  async getClientContacts(clientId, config = {}) {
    return await this._get(`/clients/${clientId}/contacts`, config);
  }

  async addClientContact(clientId, data, config = {}) {
    return await this._post(`/clients/${clientId}/contacts`, data, config);
  }

  async updateClientContact(contactId, data, config = {}) {
    return await this._patch(`/client-contacts/${contactId}`, data, config);
  }

  async deleteClientContact(contactId, config = {}) {
    return await this._delete(`/client-contacts/${contactId}`, config);
  }

  async getOnboardingState(config = {}) {
    return await this._get('/user-onboarding', { ...config, silent: true });
  }

  async updateOnboardingState(patch, config = {}) {
    return await this._patch('/user-onboarding', patch, { ...config, silent: true });
  }
}

export const api = new Api();
